import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useHypoteseConfig, type HypothesisConfig } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { useMoeteSkjerm, type MeetingScreenSlide } from '../api/useMoeter.ts';
import { useSidetittel } from '../skall/tittel.ts';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel, tokenVar } from '../status/status.ts';
import { kildestatus, resultatOrd } from './kilder.ts';
import { nummerTekst } from './Kvittering.tsx';
import { fornavn, navnAv, statusNavn, stor } from './lesning.ts';
import { langDato, moeteNavn, skjermUtfall, utfallOppforing } from './moeteLesning.ts';
import { useMoeteKanal } from './moeteSynk.ts';
import { kortDato, retningOrd, tall } from './setning.ts';

/**
 * Skjermen i rommet (H3.2, W6–W7; DD-47 presisert 2026-10-29): en egen rute UTEN skall, i et eget
 * vindu som følger kjøringen gjennom BroadcastChannel. Den leser bare den lukkede `MeetingScreen`
 * — ingen tall fra møtet, ingen notater, ingen svarutkast (P1) — så de finnes ikke i DOM-en her.
 *
 * Én hypotese om gangen i fast rekkefølge: påstanden til venstre (setningen i `h1`, tallet testen
 * lover i `display` som skjermens ene helt, kilden og hva vi mister), grunnlaget til høyre (antallet,
 * sitatene, «Vurdert før», anbefalingen), og utfallspillen når den er registrert (P7). Hele flaten
 * er panelets typeskala ganget med `skala.presentasjon` (P5) — klassen velges ved å måle bredden mot
 * `--innspel-presentasjon-stor-fra` med matchMedia, som panelets breakpoint (DD-51).
 *
 * Tastene er bare ← → og Esc.
 */
export function SkjermenIRommet({ moeteId }: { moeteId: string }) {
  const skjerm = useMoeteSkjerm(moeteId);
  const config = useHypoteseConfig();
  const me = useMe();
  const [valgtId, setValgtId] = useState<string | null>(null);
  const stor = useStorFlate();
  const slides = useMemo(() => skjerm.data?.slides ?? [], [skjerm.data]);
  const indeks = Math.max(0, slides.findIndex((s) => s.hypothesisId === valgtId));
  const slide = slides[indeks];
  useSidetittel(skjerm.data ? `Skjermen i rommet · ${moeteNavn(skjerm.data.meeting)}` : 'Skjermen i rommet', me.data?.tenantName);

  const send = useMoeteKanal(moeteId, (m) => {
    if (m.type === 'vis') setValgtId(m.hypoteseId);
    else if (m.type === 'utfall') void skjerm.refetch();
    else if (m.type === 'lukk') window.close();
    else if (m.type === 'ping') send({ type: 'her' });
  });

  // Meld at skjermen er åpen, så kjøringen svarer med raden den står på; og at den er borte.
  useEffect(() => {
    send({ type: 'her' });
    const borte = () => send({ type: 'borte' });
    window.addEventListener('pagehide', borte);
    return () => {
      window.removeEventListener('pagehide', borte);
      borte();
    };
  }, [send]);

  const bla = useCallback(
    (steg: number) => {
      const til = slides[Math.min(slides.length - 1, Math.max(0, indeks + steg))];
      if (!til || til === slide) return;
      setValgtId(til.hypothesisId);
      send({ type: 'vis', hypoteseId: til.hypothesisId });
    },
    [slides, indeks, slide, send],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        bla(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        bla(-1);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        window.close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bla]);

  const klasse = `skr ${stor ? 'skr-stor' : 'skr-liten'}`;

  if (skjerm.isPending) {
    return (
      <main className={klasse} data-testid="skr-flate" aria-busy="true">
        <p role="status" className="skr-melding">
          Henter skjermen i rommet …
        </p>
      </main>
    );
  }
  if (skjerm.isError || !skjerm.data) {
    return (
      <main className={klasse} data-testid="skr-flate">
        <p role="alert" className="skr-melding">
          <Ikon navn="information-circle-outline" />
          Skjermen i rommet kunne ikke hentes. Lukk vinduet og åpne den igjen fra kjøringen.
        </p>
      </main>
    );
  }
  const d = skjerm.data;
  if (!slide) {
    return (
      <main className={klasse} data-testid="skr-flate">
        <p role="status" className="skr-melding">
          Ingen hypoteser å vise. Agendaen er tom.
        </p>
      </main>
    );
  }

  return (
    <main className={klasse} data-testid="skr-flate" aria-labelledby="skr-setning">
      <header className="skr-topp">
        <span className="skr-moete">
          {me.data?.tenantName ? <b>{me.data.tenantName}</b> : null}
          {me.data?.tenantName ? <span aria-hidden="true"> · </span> : null}
          {moeteNavn(d.meeting)}
        </span>
        <span className="skr-hvem">
          {nummerTekst(slide.number)} · foreslått av {navnAv(slide.submittedBy)}
          {slide.reviewer ? ` · vurdert av ${navnAv(slide.reviewer)}` : ''}
        </span>
      </header>
      <div className="skr-kolonner">
        <section className="skr-venstre" aria-label="Påstanden">
          {slide.sourceStatus === 'maa_bygges' ? (
            <p className="skr-merke skr-merke-varsel">
              <Ikon navn="hammer-outline" />
              Måling må bygges før testen kan starte
            </p>
          ) : null}
          <h1 id="skr-setning" className="skr-setning">
            {slide.sentence}
          </h1>
          <Tallet s={slide} />
          <dl className="skr-fakta">
            {slide.sourceText ? (
              <div className="skr-fakta-rad">
                <dt>Kilde</dt>
                <dd>
                  {slide.sourceText}
                  {slide.updateFrequency ? ` · oppdateres ${FREKVENS[slide.updateFrequency] ?? ''}` : ''}
                  <Kildestatus s={slide} />
                </dd>
              </div>
            ) : null}
            {slide.whatWeLose ? (
              <div className="skr-fakta-rad">
                <dt>Hvis vi venter</dt>
                <dd>{slide.whatWeLose}</dd>
              </div>
            ) : null}
          </dl>
        </section>
        <section className="skr-hoyre" aria-label="Grunnlaget">
          {slide.outcome ? <Utfallspille s={slide} decider={d.decider} config={config.data} /> : null}
          <p className="skr-grunnlag" data-testid="skr-grunnlag">
            <b>{slide.evidenceCount}</b> {slide.evidenceCount === 1 ? 'innspill fra brukere' : 'innspill fra brukere'}
          </p>
          {slide.quotes?.length ? (
            <ul className="skr-sitater" aria-label="Sitater">
              {slide.quotes.map((q) => (
                <li key={q.submissionId} className="skr-sitat" data-testid="skr-sitat">
                  <blockquote>«{q.text}»</blockquote>
                  <span className="skr-sitat-meta">
                    Innspill {langDato(q.submittedOn)}
                    {q.platform ? ` · ${PLATTFORM[q.platform] ?? q.platform}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <VurdertFoer s={slide} config={config.data} />
          {slide.recommendation ? (
            <div className="skr-anbefaling">
              <Ikon navn="document-text-outline" />
              <span>
                <b>Anbefaling fra {navnAv(slide.recommendation.by)}</b> {slide.recommendation.text}
              </span>
            </div>
          ) : null}
        </section>
      </div>
      <footer className="skr-fot">
        <span>{`Registreres av ${navnAv(d.registeredBy)} på vegne av ${navnAv(d.decider)}. Ingen får svar før ${navnAv(d.decider)} har bekreftet.`}</span>
        <span className="skr-fot-hoyre">
          <span aria-hidden="true">
            <kbd>←</kbd> <kbd>→</kbd> blar · <kbd>Esc</kbd> lukker
          </span>
          <b>{`${indeks + 1} av ${slides.length}`}</b>
        </span>
      </footer>
    </main>
  );
}

const FREKVENS: Record<string, string> = { daglig: 'daglig', ukentlig: 'ukentlig', maanedlig: 'månedlig', kvartalsvis: 'kvartalsvis', sjeldnere: 'sjeldnere enn kvartalsvis' };
const PLATTFORM: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web', tvos: 'tvOS', androidtv: 'Android TV', tizen: 'Tizen', webos: 'webOS' };

/**
 * Tallet (P5, DD-67): målet testen lover er skjermens ene `display`; dagens verdi står i `h1`-størrelse
 * foran pilen, datoene under i `small`. Klienten bygger linjen av feltene, regner aldri.
 */
function Tallet({ s }: { s: MeetingScreenSlide }) {
  const maalt = s.baselineState === 'maalt' && s.baselineValue != null;
  const enhet = (v: number) => `${tall(v)} ${s.unit}`.trim();
  return (
    <div className="skr-tallet" data-testid="skr-tall">
      <p className="skr-tallrad">
        <span className="skr-idag">{maalt ? enhet(s.baselineValue!) : 'ikke målt'}</span>
        <span className="skr-pil" aria-hidden="true">
          →
        </span>
        <span className="skr-display">{`${retningOrd(s.direction)} ${enhet(s.threshold)}`}</span>
      </p>
      <p className="skr-datoer">{`${maalt ? `i dag, målt ${langDato(s.baselineOn)}` : 'baseline mangler'} · innen ${langDato(s.testEndsOn)}`}</p>
    </div>
  );
}

function Kildestatus({ s }: { s: MeetingScreenSlide }) {
  const k = kildestatus(s.sourceStatus);
  const hvem = s.sourceVerifiedBy ? ` — ${navnAv(s.sourceVerifiedBy)}${s.sourceVerifiedAt ? `, ${langDato(s.sourceVerifiedAt.slice(0, 10))}` : ''}` : '';
  return (
    <span className="skr-kildestatus" style={{ '--skr-tone': tokenVar(k.fg) } as CSSProperties}>
      {' · '}
      <Ikon navn={k.ikon} />
      {s.sourceStatus === 'verifisert' ? `definisjonen er avklart${hvem}` : k.etikett.toLowerCase()}
    </span>
  );
}

/** Pillen etter registrering (P7): utfallet med ikon og ord — aldri tallene, aldri svaret. */
function Utfallspille({ s, decider, config }: { s: MeetingScreenSlide; decider: { id: string; displayName?: string }; config: HypothesisConfig | undefined }) {
  const o = s.outcome!;
  const u = utfallOppforing(o.outcome);
  return (
    <p className="skr-utfall" data-testid="skr-utfall" style={{ '--pille-fg': tokenVar(u.fg), '--pille-bg': tokenVar(u.bg) } as CSSProperties}>
      <Ikon navn={u.ikon} />
      {skjermUtfall(o, decider, config)}
    </p>
  );
}

/** «Vurdert før» (H3.3 W11): kortet slik det er tegnet der, med innsenderens egen setning. */
function VurdertFoer({ s, config }: { s: MeetingScreenSlide; config: HypothesisConfig | undefined }) {
  const k = s.supersedesCard;
  if (!k) return null;
  const o = hypoteseOppforing(hypoteseStatusNoekkel(k.status, k.outcome));
  const linje = [nummerTekst(k.hypothesis.number), k.hypothesis.title, k.result ? resultatOrd(k.result, ', ') : null, kortDato(k.on)].filter(Boolean).join(' · ');
  return (
    <div className="skr-foer">
      <p className="skr-foer-tittel">
        <Ikon navn="list-outline" />
        Bygger på en som er vurdert før
      </p>
      <p className="skr-foer-linje">
        <span className="skr-foer-status" style={{ '--skr-tone': tokenVar(o.fg) } as CSSProperties}>
          <Ikon navn={o.ikon} />
          {statusNavn(config, k)}
        </span>
        <span>{linje}</span>
      </p>
      {s.differenceNote ? (
        <p className="skr-foer-annerledes">
          <Ikon navn="person-outline" />
          <span>
            <b>{stor(fornavn(s.submittedBy))}:</b> {s.differenceNote}
          </span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Faktoren (P5): 1,75 fra `--innspel-presentasjon-stor-fra`, ellers 1,25. Grensen leses fra
 * tokens.css og måles med matchMedia, fordi en `@media`-betingelse ikke kan lese en variabel.
 */
function useStorFlate(): boolean {
  const [stor, setStor] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const grense = getComputedStyle(document.documentElement).getPropertyValue('--innspel-presentasjon-stor-fra').trim();
    if (!grense) return;
    const mq = matchMedia(`(min-width: ${grense})`);
    const oppdater = () => setStor(mq.matches);
    oppdater();
    mq.addEventListener('change', oppdater);
    return () => mq.removeEventListener('change', oppdater);
  }, []);
  return stor;
}
